import {
  AfterViewInit,
  ContentChild,
  Directive,
  Input,
  OnDestroy,
} from '@angular/core';
import { SearchType, TableColumnFilterItem, TableWidth } from '../domain/types';
import { TableCellTemplateDirective } from './table-cell-template.directive';
import { TableColumnCustomHeaderDirective } from './table-column-custom-header.directive';
import { TableColumnFilterDirective } from './table-column-filter.directive';
import { TableColumnFooterDirective } from './table-column-footer.directive';
import { BehaviorSubject, Observable, of, Subscriber } from 'rxjs';
import { map } from 'rxjs/operators';
import { TableColumnHeaderOptionDirective } from './table-column-header-option.directive';

@Directive({
  selector: '[psUiTableColumn]',
})
export class TableColumnDirective implements AfterViewInit, OnDestroy {
  @Input()
  public title!: string;

  @Input()
  public set show(status: boolean) {
    this.showCommand.next(status);
  }

  @Input()
  public field: string = '';

  @Input()
  public searchable = false;

  @Input()
  public multi = true;

  @Input()
  public searchDropdownItems: boolean = false;

  @Input()
  public showClearStateButton: boolean = false;

  @Input()
  public sortable = false;

  @Input()
  public searchType: SearchType = 'start';

  @Input()
  public width?: TableWidth;

  @ContentChild(TableCellTemplateDirective)
  public content!: TableCellTemplateDirective;

  @ContentChild(TableColumnCustomHeaderDirective)
  public header?: TableColumnCustomHeaderDirective;

  @ContentChild(TableColumnFilterDirective)
  public filter?: TableColumnFilterDirective;

  @ContentChild(TableColumnFooterDirective)
  public footer?: TableColumnFooterDirective;

  public filters$: Observable<TableColumnFilterItem[]>;
  public groups$: Observable<Record<number, string>>;

  public get template() {
    return this.content.template;
  }

  public get delegate() {
    return this.filter?.delegate;
  }

  public get show$() {
    return this.showCommand.asObservable();
  }

  public get filterable$() {
    return this.filter?.show$ ?? of(false);
  }

  private readonly showCommand = new BehaviorSubject<boolean>(true);
  private readonly filterCommand = new BehaviorSubject<
    TableColumnHeaderOptionDirective[]
  >([]);
  private readonly _subscriber = new Subscriber();

  constructor() {
    this.filters$ = this.filterCommand.pipe(
      map((options) => options.map((element) => element.option()))
    );

    this.groups$ = this.filterCommand.pipe(
      map((options) =>
        options.reduce(
          (acc, next, index) => ({ ...acc, [index]: next.group }),
          {}
        )
      )
    );
  }

  public ngAfterViewInit(): void {
    this._subscriber.add(
      this.filter?.options.changes.subscribe((query) => {
        return this.filterCommand.next(query);
      })
    );

    this.filter?.options.notifyOnChanges();
  }

  public ngOnDestroy() {
    this._subscriber.unsubscribe();
  }

  public space(): string | null {
    if (!this.width) {
      return null;
    }

    if (this.width === 'fit') {
      return this.filter ? '15%' : '1%';
    }

    if (this.width.toString().includes('%')) {
      return this.width.toString();
    }

    return `${this.width}px`;
  }
}
